/*
 * Purpose: Gmail OAuth (GIS) — connect inbox for native API reads
 * Last modified: 2026-06-25 | Agent
 * Completeness: 80
 */
import { useSecretStore } from '@/stores/secretStore';
import { resolveSecret } from '@/lib/resolveSecret';

const GMAIL_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly';
const SCRIPT_ID = 'google-gsi-script';

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }) => { requestAccessToken: () => void };
        };
      };
    };
  }
}

function getClientId(): string | null {
  return resolveSecret('GOOGLE_CLIENT_ID');
}

export function hasGoogleOAuthConfig(): boolean {
  return Boolean(getClientId());
}

function loadGsiScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GSI script failed')));
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
}

export async function connectGmailOAuth(): Promise<string> {
  const clientId = getClientId();
  if (!clientId) {
    throw new Error('Add GOOGLE_CLIENT_ID to Secrets vault (Google Cloud OAuth Web client).');
  }

  await loadGsiScript();

  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: GMAIL_SCOPE,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error || 'OAuth failed'));
          return;
        }
        void useSecretStore
          .getState()
          .setSecret('GOOGLE_ACCESS_TOKEN', response.access_token)
          .then(() => resolve(response.access_token))
          .catch(reject);
      },
    });
    client.requestAccessToken();
  });
}

export function getGmailAccessToken(): string | null {
  return resolveSecret('GOOGLE_ACCESS_TOKEN');
}

export function disconnectGmailOAuth(): void {
  const store = useSecretStore.getState();
  const token = store.secrets.find((s) => s.name === 'GOOGLE_ACCESS_TOKEN');
  if (token) void store.deleteSecret(token.id);
}
